/**
 * Site addresses — shared client/server.
 *
 * Address normalisation for MATCHING — "are these two strings the same place?"
 * — and address COMPOSITION for display: "what do we put on screen?"
 *
 * One rule for every surface: the tech app, the office, and the Theo connector
 * (functions/src/mcp/faults.ts), so a fault raised from Claude carries the same
 * full address a fault raised in the app does. The client imports these through
 * `src/utils/address.ts`, which re-exports this module unchanged.
 *
 * NOT the same as `normalizeAddress` in geocode.service.ts — that one is a
 * geocode CACHE KEY and deliberately strips less (only `.` and `,`). Two
 * different jobs that happen to share a name; don't merge them.
 */

/**
 * Lower-case, strip separator punctuation, collapse whitespace.
 * "13 School Ln., EXETER NSW 2579" → "13 school ln exeter nsw 2579"
 */
export function normalizeAddress(addr: string): string {
  return (addr || '')
    .toLowerCase()
    .replace(/[,./\\#-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// ─── Composing an address for display ───────────────────────────────────────
//
// Every record type in this app splits the address the same way: a street line
// in `address`, and the locality in separate `city` / `state` / `postcode`
// fields. Rendering the street line alone — which is what most surfaces did —
// drops the town. "13 School Ln" is not an address anyone can drive to, and
// half the customer base lives on a street name that repeats in three towns.
//
// These helpers are the single answer to "show me the whole address". All
// pure — unit-tested in shared/src/domain/__tests__/site-address.test.ts,
// src/utils/__tests__/address.test.ts and services/__tests__/job-address.test.ts.

/** Loose shape of the address parts a job / customer / address-book row carries. */
export interface SiteAddressParts {
  address?: string | null;
  loc?: string | null;
  city?: string | null;
  state?: string | null;
  postcode?: string | null;
}

function tidy(value: string | null | undefined): string {
  return (value || '').trim();
}

/**
 * True when `haystack` already contains `needle` as a run of whole tokens.
 *
 * Token-exact, NOT substring: a plain `includes` says "13 Sandy Bay Rd"
 * contains the state "SA", and we'd then drop the real state off the composed
 * address.
 */
export function containsPhrase(haystack: string, needle: string): boolean {
  const need = normalizeAddress(needle).split(' ').filter(Boolean);
  if (need.length === 0) return true;
  const hay = normalizeAddress(haystack).split(' ').filter(Boolean);
  for (let i = 0; i + need.length <= hay.length; i++) {
    if (need.every((tok, j) => hay[i + j] === tok)) return true;
  }
  return false;
}

/**
 * The town a record names in its own `city` / `loc` — or '' when that slot
 * holds something that isn't a town.
 *
 * `loc` is meant to be the suburb, but fault jobs were written with the whole
 * fault address in it (`loc: fault.address`), and older faults saved only the
 * street. So a `loc` can be:
 *   - the street line itself ("1427 Illawarra Highway") — not a town;
 *   - a formatted address ("1427 Illawarra Highway, Wildes Meadow NSW 2577",
 *     "Unit 3, 12 Bong Bong St, Bowral NSW 2576") — the town is the locality
 *     at the end (see `localityOfFormatted`);
 *   - a street with no comma that isn't the job's own street — a house number
 *     is never the start of a town name.
 */
function explicitSuburb(parts: SiteAddressParts): string {
  const raw = tidy(parts.city) || tidy(parts.loc);
  if (!raw) return '';
  const street = tidy(parts.address);
  if (street && normalizeAddress(raw) === normalizeAddress(street)) return '';
  if (raw.includes(',')) return localityOfFormatted(raw);
  if (/^\d/.test(raw)) return '';
  return raw;
}

/** "NSW", "2577", "NSW 2577" — a segment that names no town on its own. */
function isStatePostcodeOnly(segment: string): boolean {
  const tokens = segment.split(/\s+/).filter(Boolean);
  return tokens.length > 0 && tokens.every((t) => /^\d+$/.test(t) || /^[A-Z]{2,3}$/.test(t));
}

/**
 * "Unit 3", "Lot 5", "Shop 2", "Level 1", "Suite 4B" — a segment that only
 * names a part of a site. The street is in the segment after it.
 */
function isSubPremise(segment: string): boolean {
  return /^(unit|u|lot|shop|suite|ste|level|lvl|floor|apartment|apt|flat|villa|townhouse|factory|warehouse|office|building|bldg)\.?\s*[\w/-]+$/i.test(
    segment,
  );
}

/**
 * The locality of a comma-formatted address — the town and whatever follows
 * it — or '' when there isn't one.
 *
 * Read from the END, not from the first comma: the street line can itself hold
 * commas ("Unit 3, 12 Bong Bong St, Bowral NSW 2576", "Lot 5, Kangaloon Rd,
 * Robertson NSW 2577"), and taking everything after the first comma hands back
 * a street. Walk back over a bare "NSW 2577" segment (the town is the one
 * before it) and a trailing country after a postcode, stop at the first segment
 * that names a town. The street line — any leading Unit/Lot/Shop parts plus
 * the street segment after them — is never the answer, so "Lot 5, Kangaloon
 * Rd" has no town at all.
 */
function localityOfFormatted(formatted: string): string {
  const segs = formatted.split(',').map((s) => s.trim()).filter(Boolean);
  let street = 0;
  while (street < segs.length - 1 && isSubPremise(segs[street])) street++;
  let i = segs.length - 1;
  // "…, Exeter NSW 2579, Australia" — a country after a postcode isn't a town.
  if (i >= street + 2 && !/\d/.test(segs[i]) && /\d$/.test(segs[i - 1])) i--;
  while (i > street && isStatePostcodeOnly(segs[i])) i--;
  if (i <= street || /^\d/.test(segs[i])) return '';
  return segs.slice(i).join(', ');
}

/**
 * Compose "street, suburb STATE postcode" from whatever parts exist.
 *
 * Anything the street line already says is skipped — `job.address` is usually
 * the full formatted string from Google autocomplete, and "13 School Ln,
 * Exeter NSW 2579, Exeter NSW 2579" is worse than no suburb at all. With no
 * street the suburb stands alone, which is exactly the old behaviour.
 */
export function composeSiteAddress(parts: SiteAddressParts): string {
  const street = tidy(parts.address);
  const suburb = explicitSuburb(parts);
  const state = tidy(parts.state);
  const postcode = tidy(parts.postcode);

  // No street: whatever the record carries stands alone, verbatim — a suburb
  // ("BOWRAL") or a whole address someone typed into `loc`.
  const head = street || tidy(parts.city) || tidy(parts.loc);
  if (!head) return '';

  const tail = [
    street && suburb && !containsPhrase(head, suburb) ? suburb : '',
    state && !containsPhrase(head, state) ? state : '',
    postcode && !containsPhrase(head, postcode) ? postcode : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (!tail) return head;
  // "5 Bourke St, Wollongong" + NSW 2500 → "5 Bourke St, Wollongong NSW 2500":
  // when the street line already ends with the town, the state and postcode
  // follow it the way they would in any written address.
  const endsWithTown = !!street && !!suburb && endsWithPhrase(street, suburb);
  return endsWithTown ? `${head} ${tail}` : `${head}, ${tail}`;
}

/** True when `haystack` ends with `needle` as whole tokens. */
function endsWithPhrase(haystack: string, needle: string): boolean {
  const need = normalizeAddress(needle).split(' ').filter(Boolean);
  const hay = normalizeAddress(haystack).split(' ').filter(Boolean);
  if (need.length === 0 || need.length > hay.length) return false;
  const start = hay.length - need.length;
  return need.every((tok, j) => hay[start + j] === tok);
}

/** Just the locality half — "Exeter NSW 2579" — for two-line address blocks. */
export function composeLocality(parts: SiteAddressParts): string {
  const street = tidy(parts.address);
  const suburb = explicitSuburb(parts);
  const state = tidy(parts.state);
  const postcode = tidy(parts.postcode);

  return [
    suburb && !(street && containsPhrase(street, suburb)) ? suburb : '',
    state && !(street && containsPhrase(street, state)) ? state : '',
    postcode && !(street && containsPhrase(street, postcode)) ? postcode : '',
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * The town on its own — for chips, map pins, the weather line and list rows
 * too narrow to carry the street. Falls back to the locality at the end of a
 * formatted street line when the record's own `city` / `loc` is missing
 * or isn't a town (see `explicitSuburb`). Never returns a street.
 */
export function addressSuburb(parts: SiteAddressParts): string {
  const explicit = explicitSuburb(parts);
  if (explicit) return explicit;

  // "13 School Ln, Exeter NSW 2579" → "Exeter NSW 2579". Best effort only:
  // a street line with no commas has nothing to give.
  const street = tidy(parts.address);
  if (!street.includes(',')) return '';
  return localityOfFormatted(street);
}

/** Address-book row / legacy single-address block on a customer record. */
interface CustomerAddressLike extends SiteAddressParts {
  isPrimary?: boolean;
}

/**
 * Just enough of a Customer to read an address off — the legacy single-address
 * fields plus the `addresses[]` book.
 */
export interface CustomerAddressSource {
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postcode?: string | null;
  /** Both spellings exist on the schema; different forms write different ones. */
  zipCode?: string | null;
  addresses?: CustomerAddressLike[];
}

/**
 * The customer's main address, composed in full.
 *
 * The legacy single-address fields mirror whichever book entry is Main
 * (`legacyAddressMirror` in customer.service.ts), so they're the fast path.
 * The book is the fallback for records written before the mirror existed.
 */
export function formatCustomerAddress(
  customer: CustomerAddressSource | null | undefined,
): string {
  if (!customer) return '';

  const legacy = composeSiteAddress({
    address: customer.address,
    city: customer.city,
    state: customer.state,
    postcode: customer.postcode || customer.zipCode,
  });
  if (legacy) return legacy;

  const rows = customer.addresses ?? [];
  const chosen = rows.find((r) => r.isPrimary && tidy(r.address)) || rows.find((r) => tidy(r.address));
  return chosen ? composeSiteAddress(chosen) : '';
}

/**
 * The town/state/postcode half of the customer's main address — for the
 * two-line address blocks on customer cards, where the street already has its
 * own line above.
 */
export function formatCustomerLocality(
  customer: CustomerAddressSource | null | undefined,
): string {
  if (!customer) return '';

  const legacy = composeLocality({
    address: customer.address,
    city: customer.city,
    state: customer.state,
    postcode: customer.postcode || customer.zipCode,
  });
  if (legacy) return legacy;

  const rows = customer.addresses ?? [];
  const chosen = rows.find((r) => r.isPrimary && tidy(r.address)) || rows.find((r) => tidy(r.address));
  return chosen ? composeLocality(chosen) : '';
}

/** Deal address fields, which use their own `job*` prefix. */
export interface DealAddressSource {
  jobAddress?: string | null;
  jobCity?: string | null;
  jobState?: string | null;
  jobZipCode?: string | null;
}

/**
 * The deal's site address, composed in full.
 *
 * `deal.jobAddress` is the street line the wizard copied off the chosen
 * address-book row; the town went into `jobCity` and got left behind by every
 * surface that rendered `jobAddress` on its own.
 */
export function formatDealAddress(deal: DealAddressSource | null | undefined): string {
  if (!deal) return '';
  return composeSiteAddress({
    address: deal.jobAddress,
    city: deal.jobCity,
    state: deal.jobState,
    postcode: deal.jobZipCode,
  });
}
